import React, { useState, useEffect, useRef } from 'react';
import { PREMIUM_PRICE, PREMIUM_PRICE_PER_MONTH, TRIAL_CTA, TRIAL_DAYS, TRIAL_TERMS } from "../config/pricing";
import { useAuth } from '../context/AuthContext';
import { useLocation, Link } from 'react-router-dom';
import {
    Container, Typography, Box, Paper, Button,
    Card, CardContent, List, ListItem, 
    ListItemIcon, ListItemText, CircularProgress,
    Snackbar, Alert, Chip
} from '@mui/material';
import { 
    CheckCircle, WorkspacePremium as PremiumIcon, Cancel
} from '@mui/icons-material';
import { config } from '../Constants';
import { formatDateOnly } from '../utils/dateOnly';

const URL = config.url;

const DiscordIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
);

const SubscriptionPage = () => {
    const { user, isAuthenticated, isPremium, login, authToken, validateToken } = useAuth();
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const location = useLocation();
    const successHandledRef = useRef(false);

    const canUseTrial = !user?.hasUsedFreeTrial;
    const isSuccessRedirect = location.pathname === '/subscription/success';
    const isCanceledRedirect = location.pathname === '/subscription/canceled';

    useEffect(() => {
        const savedRedirect = localStorage.getItem('redirectAfterLogin');
        if (savedRedirect) {
            localStorage.removeItem('redirectAfterLogin');
        }
    }, [isAuthenticated, location]);

    useEffect(() => {
        const processSuccessfulSubscription = async () => {
            if (!isSuccessRedirect || !isAuthenticated || successHandledRef.current) {
                return;
            }

            const sessionId = new URLSearchParams(location.search).get('session_id');
            if (!sessionId) {
                showSnackbar('Missing Stripe session ID after checkout.', 'error');
                return;
            }

            successHandledRef.current = true;
            setLoading(true);

            try {
                const response = await fetch(`${URL}/api/v1/subscription/success?session_id=${encodeURIComponent(sessionId)}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authToken}`
                    }
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => null);
                    throw new Error(errorData?.message || 'Failed to finalize subscription');
                }

                await validateToken();
                showSnackbar('Premium subscription activated successfully.', 'success');
            } catch (error) {
                console.error('Error finalizing subscription:', error);
                showSnackbar(error.message || 'Failed to finalize subscription', 'error');
            } finally {
                setLoading(false);
            }
        };

        processSuccessfulSubscription();
    }, [authToken, isAuthenticated, isSuccessRedirect, location.search, validateToken]);

    useEffect(() => {
        if (isCanceledRedirect) {
            showSnackbar('Checkout was canceled before activation.', 'info');
        }
    }, [isCanceledRedirect]);

    const handleUpgradeClick = async () => {
        if (!isAuthenticated) {
            localStorage.setItem('redirectAfterLogin', '/subscription');
            if (canUseTrial) {
                localStorage.setItem('pendingFreeTrial', 'true');
            }
            login();
            return;
        }

        setLoading(true);
        try {
            const trialParam = canUseTrial ? '?trial=true' : '';
            const response = await fetch(`${URL}/api/v1/subscription/create-checkout${trialParam}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                window.location.href = data.data.sessionUrl;
            } else {
                console.error('Error creating checkout session');
                showSnackbar('Error creating checkout session', 'error');
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            showSnackbar('Error creating checkout session', 'error');
        } finally {
            setLoading(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const premiumExpiryLabel = formatDateOnly(user?.premiumExpiry);

    if (isPremium) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', mb: 4 }}>
                    <CheckCircle color="success" sx={{ fontSize: 56, mb: 2 }} />
                    <Typography variant="h4" gutterBottom>
                        You're a Premium Member!
                    </Typography>
                    <Typography variant="body1" paragraph>
                        Thank you for supporting StockMaid. You have full access to all premium features.
                    </Typography>
                    {premiumExpiryLabel && (
                        <Typography variant="body2" color="text.secondary">
                            Your premium subscription is active until {premiumExpiryLabel}
                        </Typography>
                    )}
                </Paper>
                
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        component={Link}
                        to="/my-tracked-stocks"
                        sx={{ mt: 2 }}
                    >
                        Manage Tracked Products
                    </Button>
                </Box>
                
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        );
    }

    const benchmarkCards = [
        {
            id: 'stockmaid',
            label: 'StockMaid',
            time: '12:19 PM',
            delta: 'Fastest',
            deltaTone: 'success',
            image: '/images/alert-stockmaid.webp',
            highlight: true
        },
        {
            id: 'nowinstock',
            label: 'NowInStock',
            time: '12:19 PM',
            delta: 'Same minute',
            deltaTone: 'info',
            image: '/images/alert-nowinstock.webp'
        },
        {
            id: 'trackalacker',
            label: 'TrackaLacker',
            time: '12:20 PM',
            delta: '+1 min',
            deltaTone: 'error',
            image: '/images/alert-trackalacker.webp'
        }
    ];

    return (
        <Box
            sx={{
                py: { xs: 6, md: 10 },
                background: 'var(--sm-page-gradient)'
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                    <Chip
                        label="Simple, transparent pricing"
                        sx={{
                            bgcolor: 'rgba(72, 110, 255, 0.12)',
                            color: 'primary.main',
                            fontWeight: 600
                        }}
                    />
                    <Typography variant="h3" align="center" sx={{ fontWeight: 700, mt: 2 }}>
                        Pick the plan that fits your tracking style
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 360px))' },
                        gap: 3,
                        justifyContent: 'center',
                        mb: 4
                    }}
                >
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        backgroundImage: 'none',
                        boxShadow: '0 18px 40px rgba(88, 101, 242, 0.12)'
                    }}
                >
                    <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                        <Box
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                                pt: 2
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/pricing-discord.webp"
                                alt="Discord notifications preview"
                                sx={{
                                    width: '100%',
                                    display: 'block',
                                    mt: -2
                                }}
                            />
                        </Box>
                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Free
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 2, fontWeight: 700 }}>
                            $0
                        </Typography>
                        <Typography variant="body2">
                            Basic access forever.
                        </Typography>
                        <List sx={{ mt: 2, mb: 3 }}>
                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <CheckCircle color="success" />
                                </ListItemIcon>
                                <ListItemText primary="Discord notifications" />
                            </ListItem>
                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <Cancel sx={{ color: 'var(--sm-danger)' }} />
                                </ListItemIcon>
                                <ListItemText primary="Email notifications" />
                            </ListItem>
                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <Cancel sx={{ color: 'var(--sm-danger)' }} />
                                </ListItemIcon>
                                <ListItemText primary="In-store pickup zip codes" />
                            </ListItem>
                        </List>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                component={Link}
                                to="https://discord.gg/W78pdPCdbC"
                                target="_blank"
                                rel="noreferrer"
                                startIcon={<DiscordIcon />}
                                sx={{
                                    bgcolor: '#5865F2',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    borderRadius: 2,
                                    py: 1.1,
                                    '&:hover': { bgcolor: '#4752C4' }
                                }}
                            >
                                Join the Discord
                            </Button>
                        </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'primary.main',
                        position: 'relative',
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        backgroundImage: 'none',
                        boxShadow: '0 24px 55px rgba(91, 140, 255, 0.2)'
                    }}
                >
                    <CardContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                        <Box
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                                pt: 2
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/pricing-email.webp"
                                alt="Premium email alerts preview"
                                sx={{
                                    width: '100%',
                                    display: 'block',
                                    mt: -2
                                }}
                            />
                        </Box>
                        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Premium
                        </Typography>
                        <Typography variant="h4" sx={{ mt: 2, fontWeight: 700 }}>
                            {canUseTrial ? 'Free' : PREMIUM_PRICE}
                            <Typography component="span" variant="subtitle2">
                                {canUseTrial ? ` for ${TRIAL_DAYS} days` : '/month'}
                            </Typography>
                        </Typography>
                        {canUseTrial && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                then {PREMIUM_PRICE_PER_MONTH}
                            </Typography>
                        )}
                        <List sx={{ mt: 2, mb: 3 }}>
                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <CheckCircle color="success" />
                                </ListItemIcon>
                                <ListItemText primary="Discord notifications" />
                            </ListItem>
                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <CheckCircle color="success" />
                                </ListItemIcon>
                                <ListItemText primary="Email notifications" />
                            </ListItem>
                            <ListItem disableGutters>
                                <ListItemIcon>
                                    <CheckCircle color="success" />
                                </ListItemIcon>
                                <ListItemText primary="In-store pickup zip codes" />
                            </ListItem>
                        </List>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handleUpgradeClick}
                                disabled={loading}
                                startIcon={<PremiumIcon />}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : (canUseTrial ? TRIAL_CTA : 'Subscribe Now')}
                            </Button>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                {canUseTrial ? `${TRIAL_TERMS} ` : 'Cancel anytime. '}Secure payment via Stripe.
                            </Typography>
                        </Box>
                        </Box>
                    </CardContent>
                </Card>

                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        mt: { xs: 4, md: 5 },
                        p: { xs: 2.5, md: 3.5 },
                        bgcolor: '#0f172a',
                        color: 'common.white',
                        border: '1px solid',
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                        borderRadius: 3,
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 30px 80px rgba(15, 23, 42, 0.35)'
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            background:
                                'radial-gradient(circle at 20% 20%, rgba(88, 101, 242, 0.28), transparent 50%), radial-gradient(circle at 80% 0%, rgba(33, 182, 183, 0.2), transparent 45%)',
                            pointerEvents: 'none'
                        }}
                    />
                    <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 3 }, position: 'relative', zIndex: 1 }}>
                        <Chip
                            label="Discord notification benchmarks"
                            sx={{
                                bgcolor: 'rgba(88, 101, 242, 0.2)',
                                color: 'common.white',
                                fontWeight: 600,
                                mb: 1.5
                            }}
                        />
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Discord Notification Speed Benchmarks
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.85 }}>
                            Captured from Discord alerts for the same restock: StockMaid hits at 12:19 PM, TrackaLacker at 12:20 PM.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: { xs: 2.5, md: 3 },
                            alignItems: 'stretch',
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        {benchmarkCards.map((card) => (
                            <Box
                                key={card.id}
                                sx={{
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: card.highlight ? 'rgba(88, 101, 242, 0.8)' : 'rgba(148, 163, 184, 0.35)',
                                    bgcolor: 'background.paper',
                                    color: 'text.primary',
                                    boxShadow: card.highlight
                                        ? '0 18px 32px rgba(59, 130, 246, 0.18)'
                                        : '0 10px 24px rgba(15, 23, 42, 0.06)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden'
                                }}
                            >
                                <Box sx={{ p: 2.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            {card.label}
                                        </Typography>
                                        <Typography variant="caption">
                                            Discord alert time
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {card.time}
                                        </Typography>
                                        <Chip
                                            label={card.delta}
                                            color={card.deltaTone}
                                            size="small"
                                            sx={{ mt: 0.5, fontWeight: 600 }}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    sx={{
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'var(--background-color-dark)'
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={card.image}
                                        alt={`${card.label} alert screenshot`}
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Paper>

            </Container>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SubscriptionPage;
